<script lang="ts">
  import { Signos, Notas, type Signo } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import FisioterapiaEvaluacionesPanel from './FisioterapiaEvaluacionesPanel.svelte';
  import { isClinicaFisioterapia } from '../lib/fisioterapia';
  import { evaluacionesDesdeNotas } from '../lib/fisioterapia-evaluaciones';
  import { session } from '../lib/auth.svelte';
  import { autorFisioterapista } from '../lib/fisioterapia';

  let {
    orgTipo = null,
  }: {
    orgTipo?: string | null;
  } = $props();

  const esFisio = $derived(isClinicaFisioterapia(orgTipo));
  const autor = $derived(autorFisioterapista({ usuario: session.value?.usuario }));

  let pacienteId = $state('');
  let signos = $state<Signo[]>([]);
  let notas = $state<import('../lib/api').Nota[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);

  let nuevo = $state<Signo>({
    pacienteId: '', ta: '', fc: null, fr: null, temp: null, satO2: null, peso: null, talla: null
  });

  async function cargar() {
    if (!pacienteId) { signos = []; notas = []; return; }
    loading = true; error = null;
    try {
      const [s, n] = await Promise.all([
        Signos.porPaciente(pacienteId),
        Notas.porPaciente(pacienteId).catch(() => []),
      ]);
      signos = s;
      notas = n;
    } catch (e: unknown) { error = e instanceof Error ? e.message : String(e); }
    finally { loading = false; }
  }

  async function registrar(e: SubmitEvent) {
    e.preventDefault();
    if (!pacienteId) { error = 'Selecciona un paciente.'; return; }
    error = null;
    try {
      const body: Signo = {
        pacienteId,
        ta: nuevo.ta || null,
        fc: nuevo.fc != null ? Number(nuevo.fc) : null,
        fr: nuevo.fr != null ? Number(nuevo.fr) : null,
        temp: nuevo.temp != null ? Number(nuevo.temp) : null,
        satO2: nuevo.satO2 != null ? Number(nuevo.satO2) : null,
        peso: nuevo.peso != null ? Number(nuevo.peso) : null,
        talla: nuevo.talla != null ? Number(nuevo.talla) : null
      };
      await Signos.registrar(body);
      nuevo = { pacienteId: '', ta: '', fc: null, fr: null, temp: null, satO2: null, peso: null, talla: null };
      await cargar();
    } catch (e: unknown) { error = e instanceof Error ? e.message : String(e); }
  }

  $effect(() => { if (pacienteId) cargar(); });

  const evalCount = $derived(evaluacionesDesdeNotas(notas).length);
</script>

<section>
  <header class="view-header">
    <div><h2>{esFisio ? 'Evaluaciones fisioterapia' : 'Signos vitales'}</h2><p class="muted">Registro y evolución por paciente</p></div>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if esFisio}
    <p class="hint">Registre evaluaciones por objetivo: dolor, edema, sensibilidad, fuerza, reflejos, ROM e integral. Las plantillas son editables.</p>
  {/if}

  <div class="picker-row">
    <PacientePicker bind:value={pacienteId} onPick={(id) => pacienteId = id} placeholder="Paciente…" />
    <button onclick={cargar} disabled={loading || !pacienteId}>{loading ? '…' : 'Recargar'}</button>
  </div>

  {#if pacienteId}
    {#if esFisio}
      <FisioterapiaEvaluacionesPanel {pacienteId} {autor} {notas} onSaved={cargar} />
      <p class="meta-hist">{evalCount} evaluación(es) en expediente</p>
    {:else}
      <h3>Registrar toma</h3>
      <form onsubmit={registrar} class="form-grid">
        <label>TA <input placeholder="120/80" bind:value={nuevo.ta} /></label>
        <label>FC <input type="number" min="0" bind:value={nuevo.fc} /></label>
        <label>FR <input type="number" min="0" bind:value={nuevo.fr} /></label>
        <label>Temp °C <input type="number" step="0.1" bind:value={nuevo.temp} /></label>
        <label>SatO₂ % <input type="number" min="0" max="100" bind:value={nuevo.satO2} /></label>
        <label>Peso kg <input type="number" step="0.1" bind:value={nuevo.peso} /></label>
        <label>Talla cm <input type="number" step="0.1" bind:value={nuevo.talla} /></label>
        <button type="submit" class="full">Registrar</button>
      </form>

      <h3>Historial</h3>
      <table>
        <thead><tr><th>Fecha</th><th>TA</th><th>FC</th><th>FR</th><th>T°</th><th>SatO₂</th><th>Peso</th><th>Talla</th></tr></thead>
        <tbody>
          {#each signos as s (s.id)}
            <tr>
              <td>{s.registradoEn ? new Date(s.registradoEn).toLocaleString() : '—'}</td>
              <td>{s.ta ?? '—'}</td><td>{s.fc ?? '—'}</td><td>{s.fr ?? '—'}</td>
              <td>{s.temp ?? '—'}</td><td>{s.satO2 ?? '—'}</td>
              <td>{s.peso ?? '—'}</td><td>{s.talla ?? '—'}</td>
            </tr>
          {:else}<tr><td colspan="8" class="empty">Sin registros.</td></tr>{/each}
        </tbody>
      </table>
    {/if}
  {:else}
    <p class="hint">Selecciona un paciente para ver/registrar {esFisio ? 'evaluaciones' : 'signos vitales'}.</p>
  {/if}
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  h3 { margin: 1.5rem 0 .5rem; font-size: 1rem; color: #166534; }
  .picker-row { display: flex; gap: .5rem; align-items: center; }
  .picker-row :global(.picker) { flex: 1; }
  .form-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: .5rem; align-items: end; }
  .form-grid label { display: flex; flex-direction: column; font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .form-grid input { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827; }
  .form-grid input:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .full { grid-column: span 4; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .5rem .8rem; background: #f0fdf4; }
  td { padding: .45rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .85rem; font-variant-numeric: tabular-nums; color: #111827; }
  .empty { text-align: center; color: #9ca3af; font-style: italic; }
  .hint { color: #6b7280; font-style: italic; margin-top: 1rem; }
  .meta-hist { font-size: 0.82rem; color: #6b7280; margin-top: 0.5rem; }
  .err { color: #dc2626; }
  button { padding: .5rem .9rem; border-radius: 8px; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
</style>
